import { useCallback, useEffect, useMemo, useState } from "react";
import { AppTopBar, formatIDR } from "@/components/AppTopBar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { WithdrawalPanel } from "./WithdrawalPanel";
import { WithdrawalHistory } from "./WithdrawalHistory";

const n = (v) => parseFloat(v || 0) || 0;

export default function FinanceWithdrawal() {
  const { user } = useAuth();
  const isOwner = user?.role === "OWNER";
  const [accounts, setAccounts] = useState([]);
  const [fundRows, setFundRows] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [walletSearch, setWalletSearch] = useState("");
  const [plan, setPlan] = useState({});
  const [targetModal, setTargetModal] = useState("");
  const [targetProfit, setTargetProfit] = useState("");
  const [targetUncl, setTargetUncl] = useState("");
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [processedWallets, setProcessedWallets] = useState({});

  const loadFinancePosition = useCallback(() => {
    if (!isOwner) return;
    Promise.all([api.get("/accounts"), api.get("/mappings/stores")])
      .then(([a, m]) => {
        const nextAccounts = Array.isArray(a.data) ? a.data : [];
        setAccounts(nextAccounts);
        setFundRows(nextAccounts.map((x) => ({
          account_id: x.account_id,
          account_name: x.account_name,
          account_type: x.account_type,
          physical_balance: x.physical_balance ?? x.current_balance ?? "0",
          modal_available: x.modal_available ?? "0",
          profit_available: x.profit_available ?? "0",
          unclassified_available: x.unclassified_available ?? "0",
        })));
        setMappings(Array.isArray(m.data) ? m.data : []);
      })
      .finally(() => setAccountsLoaded(true))
      .catch(() => {});
  }, [isOwner]);

  useEffect(() => { loadFinancePosition(); }, [loadFinancePosition]);

  const handleWithdrawalChanged = () => {
    loadFinancePosition();
    setHistoryRefresh((x) => x + 1);
  };

  const mappingByAccount = useMemo(() => {
    const map = new Map();
    mappings.filter((m) => m.is_active !== false && m.account_id).forEach((m) => {
      if (!map.has(m.account_id)) map.set(m.account_id, []);
      map.get(m.account_id).push(m);
    });
    return map;
  }, [mappings]);

  const identityFor = useCallback((accountId) => {
    const ms = mappingByAccount.get(accountId) || [];
    if (!ms.length) return { label: "BELUM DIPETAKAN", store: "Belum dipetakan", marketplace: "Marketplace", warning: true };
    const first = ms[0];
    const labels = [...new Set(ms.map((m) => `${m.store_name || m.store_name_source || "Toko"} · ${m.marketplace || "MARKETPLACE"}`))];
    return {
      label: labels.join(" / "),
      store: first.store_name || first.store_name_source || "Toko",
      marketplace: first.marketplace || "Marketplace",
      warning: false,
    };
  }, [mappingByAccount]);

  const wallets = useMemo(() => {
    const map = new Map(fundRows.map((r) => [r.account_id, r]));
    return accounts
      .filter((a) => a.account_type === "MARKETPLACE_WALLET" && String(a.is_active) === "true")
      .map((a) => ({ ...a, ...(map.get(a.account_id) || {}) }))
      .filter((a) => (mappingByAccount.get(a.account_id) || []).some((m) => m.is_active !== false && m.account_id))
      .sort((a, b) => identityFor(a.account_id).label.localeCompare(identityFor(b.account_id).label));
  }, [accounts, fundRows, mappingByAccount, identityFor]);

  const visibleWallets = useMemo(() => {
    const q = walletSearch.trim().toLowerCase();
    if (!q) return wallets;
    return wallets.filter((w) => {
      const ident = identityFor(w.account_id);
      return [w.account_name, ident.store, ident.marketplace, ident.label].some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [wallets, walletSearch, identityFor]);

  const banks = accounts.filter((a) => a.account_type === "BANK" && String(a.is_active) === "true");

  const walletTotals = wallets.reduce((acc, w) => ({
    modal: acc.modal + n(w.modal_available),
    profit: acc.profit + n(w.profit_available),
    uncl: acc.uncl + n(w.unclassified_available),
    physical: acc.physical + n(w.physical_balance),
  }), { modal: 0, profit: 0, uncl: 0, physical: 0 });

  const planTotals = Object.values(plan).reduce((acc, r) => ({
    modal: acc.modal + n(r.modal),
    profit: acc.profit + n(r.profit),
    uncl: acc.uncl + n(r.uncl),
  }), { modal: 0, profit: 0, uncl: 0 });
  const planGrand = planTotals.modal + planTotals.profit + planTotals.uncl;

  const setPlanVal = (id, field, value) => {
    setPlan((p) => ({ ...p, [id]: { modal: p[id]?.modal || "", profit: p[id]?.profit || "", uncl: p[id]?.uncl || "", [field]: value } }));
  };

  const clearPlan = () => { setPlan({}); setTargetModal(""); setTargetProfit(""); setTargetUncl(""); };
  const selectedRows = wallets.filter((w) => {
    const p = plan[w.account_id] || {};
    return n(p.modal) + n(p.profit) + n(p.uncl) > 0;
  });

  const marketplaceBadge = (marketplace) => {
    const name = String(marketplace || "M").toUpperCase();
    const short = name.includes("TIKTOK") ? "T" : name.includes("SHOPEE") ? "S" : name.slice(0, 1);
    return <span style={{ width: 30, height: 30, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#f3f4f6", border: "1px solid #dfe3e8", fontWeight: 800, flex: "0 0 auto" }}>{short}</span>;
  };

  if (user && !isOwner) {
    return (<div className="app-shell"><AppTopBar active="finance-withdrawal" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="finance-withdrawal" />
      <main className="content">
        <div style={{ width: "100%", maxWidth: 1240, margin: "0 auto" }}>
          <div style={{ marginBottom: 18 }}>
            <h1 style={{ margin: "0 0 6px", fontSize: 26 }}>Penarikan Dana</h1>
            <p className="hint" style={{ margin: 0 }}>Identitas Toko/Marketplace ditampilkan bersama wallet untuk mengurangi risiko salah memilih sumber penarikan.</p>
          </div>

          <section className="panel panel--wide" style={{ marginBottom: 16, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, border: "1px solid var(--border)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>▣</div>
                <div><h2 className="panel-title" style={{ marginBottom: 2 }}>Rincian Posisi Dana per Wallet</h2><div className="hint">{wallets.length} wallet aktif</div></div>
              </div>
              <div style={{ position: "relative", minWidth: 240 }}>
                <input className="field-input" style={{ paddingLeft: 34 }} placeholder="Cari wallet / toko" value={walletSearch} onChange={(e)=>setWalletSearch(e.target.value)} />
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", opacity: .55 }}>⌕</span>
              </div>
            </div>

            <div className="table-scroll" style={{ border: "1px solid #e7eaee", borderRadius: 10, overflow: "hidden" }}><table className="data-table" style={{ margin: 0 }}>
              <thead><tr style={{ background: "#f6f8fa" }}><th>Wallet</th><th>MODAL</th><th>PROFIT</th><th>UNCLASSIFIED</th><th>TOTAL FUND</th></tr></thead>
              <tbody>
                {visibleWallets.length === 0 && <tr><td colSpan={5} className="empty-cell">Wallet tidak ditemukan.</td></tr>}
                {visibleWallets.map((w) => { const ident = identityFor(w.account_id); return <tr key={w.account_id}>
                  <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}>{marketplaceBadge(ident.marketplace)}<div><b>{w.account_name}</b><div className="hint" style={{ marginTop: 2 }}>{ident.warning ? "Belum dipetakan" : `${ident.store} · ${ident.marketplace}`}</div></div></div></td>
                  <td className="mono">{formatIDR(w.modal_available)}</td>
                  <td className="mono">{formatIDR(w.profit_available)}</td>
                  <td className="mono">{formatIDR(w.unclassified_available)}</td>
                  <td className="mono"><b>{formatIDR(n(w.modal_available)+n(w.profit_available)+n(w.unclassified_available))}</b></td>
                </tr>; })}
                {wallets.length > 0 && <tr style={{ background: "#f2faf7" }}><td><b>TOTAL</b></td><td className="mono"><b>{formatIDR(walletTotals.modal)}</b></td><td className="mono"><b>{formatIDR(walletTotals.profit)}</b></td><td className="mono"><b>{formatIDR(walletTotals.uncl)}</b></td><td className="mono"><b>{formatIDR(walletTotals.modal+walletTotals.profit+walletTotals.uncl)}</b></td></tr>}
              </tbody>
            </table></div>
            <div className="hint" style={{ marginTop: 9 }}>Saldo fisik tetap dipakai oleh mesin withdrawal canonical; tabel ini difokuskan ke komposisi fund agar lebih cepat dibaca.</div>
          </section>

          <section className="panel panel--wide" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
              <div><h2 className="panel-title" style={{ marginBottom: 4 }}>Rencana Penarikan ke BCA</h2><div className="hint">Planner saja. Belum membuat transaksi sampai Anda eksekusi di form aman bawah.</div></div>
              <button className="btn-mini" onClick={clearPlan}>Reset Rencana</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10, marginBottom: 14 }}>
              <div><label className="field-label">Kebutuhan MODAL</label><input className="field-input" type="number" min="0" value={targetModal} onChange={(e)=>setTargetModal(e.target.value)} placeholder="0" /></div>
              <div><label className="field-label">Kebutuhan PROFIT</label><input className="field-input" type="number" min="0" value={targetProfit} onChange={(e)=>setTargetProfit(e.target.value)} placeholder="0" /></div>
              <div><label className="field-label">Kebutuhan UNCLASSIFIED</label><input className="field-input" type="number" min="0" value={targetUncl} onChange={(e)=>setTargetUncl(e.target.value)} placeholder="0" /></div>
            </div>

            <div className="table-scroll"><table className="data-table">
              <thead><tr><th>Sumber</th><th>MODAL Tersedia</th><th>Tarik MODAL</th><th>PROFIT Tersedia</th><th>Tarik PROFIT</th><th>UNCLASSIFIED Tersedia</th><th>Tarik UNCLASSIFIED</th><th>Total Baris</th></tr></thead>
              <tbody>
                {wallets.map((w) => { const p = plan[w.account_id] || {}; const rowTotal=n(p.modal)+n(p.profit)+n(p.uncl); const ident=identityFor(w.account_id); return <tr key={w.account_id}>
                  <td><b>{ident.label}</b><div className="hint mono">{w.account_name}</div></td>
                  <td className="mono">{formatIDR(w.modal_available)}</td><td><input className="field-input narrow" type="number" min="0" max={n(w.modal_available)} value={p.modal || ""} onChange={(e)=>setPlanVal(w.account_id,"modal",e.target.value)} placeholder="0" /></td>
                  <td className="mono">{formatIDR(w.profit_available)}</td><td><input className="field-input narrow" type="number" min="0" max={n(w.profit_available)} value={p.profit || ""} onChange={(e)=>setPlanVal(w.account_id,"profit",e.target.value)} placeholder="0" /></td>
                  <td className="mono">{formatIDR(w.unclassified_available)}</td><td><input className="field-input narrow" type="number" min="0" max={n(w.unclassified_available)} value={p.uncl || ""} onChange={(e)=>setPlanVal(w.account_id,"uncl",e.target.value)} placeholder="0" /></td>
                  <td className="mono"><b>{formatIDR(rowTotal)}</b></td>
                </tr>; })}
                {wallets.length > 0 && <tr><td><b>TOTAL RENCANA</b></td><td></td><td className="mono"><b>{formatIDR(planTotals.modal)}</b></td><td></td><td className="mono"><b>{formatIDR(planTotals.profit)}</b></td><td></td><td className="mono"><b>{formatIDR(planTotals.uncl)}</b></td><td className="mono"><b>{formatIDR(planGrand)}</b></td></tr>}
              </tbody>
            </table></div>

            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10 }}>
              <div className="dash-fund-item modal"><span>MODAL kurang / lebih</span><b>{formatIDR(n(targetModal)-planTotals.modal)}</b></div>
              <div className="dash-fund-item profit"><span>PROFIT kurang / lebih</span><b>{formatIDR(n(targetProfit)-planTotals.profit)}</b></div>
              <div className="dash-fund-item uncl"><span>UNCLASSIFIED kurang / lebih</span><b>{formatIDR(n(targetUncl)-planTotals.uncl)}</b></div>
            </div>

            {selectedRows.length > 0 && <div className="withdraw-safe-plan">
              <div className="withdraw-safe-head">
                <div>
                  <div className="field-label">URUTAN EKSEKUSI AMAN</div>
                  <div className="hint">Satu wallet dieksekusi satu kali melalui form canonical. Selesaikan transaksi sebelumnya sebelum lanjut ke wallet berikutnya.</div>
                </div>
                <span className="withdraw-safe-count">{selectedRows.length} wallet dipilih</span>
              </div>
              <div className="withdraw-safe-table">
                <div className="withdraw-safe-grid withdraw-safe-grid-head">
                  <span>No.</span><span>Wallet</span><span>MODAL</span><span>PROFIT</span><span>UNCLASSIFIED</span><span>Total</span><span>Proses</span>
                </div>
                {selectedRows.map((w, i) => {
                  const p=plan[w.account_id] || {};
                  const ident=identityFor(w.account_id);
                  const total=n(p.modal)+n(p.profit)+n(p.uncl);
                  return <div className="withdraw-safe-grid withdraw-safe-grid-row" key={w.account_id}>
                    <span className="withdraw-safe-number">{i+1}</span>
                    <span className="withdraw-safe-wallet"><b>{ident.store}</b><small>{ident.marketplace} · {w.account_name}</small></span>
                    <span className="mono">{formatIDR(p.modal)}</span>
                    <span className="mono">{formatIDR(p.profit)}</span>
                    <span className="mono">{formatIDR(p.uncl)}</span>
                    <span className="mono withdraw-safe-total">{formatIDR(total)}</span>
                    <label className="withdraw-process-check"><input type="checkbox" checked={Boolean(processedWallets[w.account_id])} onChange={(e) => setProcessedWallets((prev) => ({ ...prev, [w.account_id]: e.target.checked }))} /><span>Diproses</span></label>
                  </div>;
                })}
              </div>
            </div>}
          </section>

          <section style={{ width: "min(1460px, calc(100vw - 40px))", marginLeft: "50%", transform: "translateX(-50%)", marginBottom: 16, paddingTop: 2 }}>
            {accountsLoaded && banks.length === 0 && <div className="alert-error">Belum ada account BANK aktif.</div>}
            <WithdrawalPanel accounts={accounts} mappings={mappings} fundRows={fundRows} onDone={handleWithdrawalChanged} />
          </section>

          <WithdrawalHistory accounts={accounts} refreshKey={historyRefresh} onChanged={handleWithdrawalChanged} />
        </div>
      </main>
    </div>
  );
}
